import React, { useEffect, useMemo, useState } from 'react'
import { Card, Table, Button, Tooltip, Form, DatePicker } from 'antd';
import apiClient from 'services/apiClient';
import Flex from 'components/shared-components/Flex';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { useMyContext } from 'Context/MyContextProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCheckDouble, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { GetParams } from '../Utils/utils';
const { RangePicker } = DatePicker;
const LiveStatus = () => {
    const { api, authToken, DownloadExcelFile, Permisson, navigate } = useMyContext();
    useEffect(() => {
        if (!Permisson?.includes('View Live Status')) {
            navigate('404')
        }
    }, [Permisson]);

    const [loading, setLoading] = useState(false);
    const [filterData, setFilterData] = useState([]);
    const [liveStatus, setLiveStatus] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [error, setError] = useState(null);

    //analytics
    const GetSettingData = async () => {
        setLoading(true);
        setError(null);
        const params = GetParams(startDate, endDate, null);
        try {
            const response = await apiClient.get(`${api}live-status`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                },
                params
            });
            const data = response.data;
            setLiveStatus((data?.LiveStatus || []).reverse());
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to fetch data');
            console.error('Error fetching live status:', error);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        GetSettingData();
    }, [startDate, endDate])


    const tableColumns = useMemo(() => [
        {
            title: 'Company Name',
            dataIndex: 'company_name',
            sorter: {
                compare: (a, b) => a.company_name.length - b.company_name.length,
            },
        },
        {
            title: 'Available Credits',
            dataIndex: 'balance',
            sorter: {
                compare: (a, b) => a.name.length - b.name.length,
            },
        },
        {
            title: 'Incoming Conv',
            dataIndex: 'total_incomingreports_count',
            sorter: {
                compare: (a, b) => a.total_incomingreports_count.length - b.total_incomingreports_count.length,
            },
        },
        {
            title: 'Campaign Conv',
            dataIndex: 'total_campaignreports_count',
            sorter: {
                compare: (a, b) => a.total_campaignreports_count.length - b.total_campaignreports_count.length,
            },
        },
        {
            title: 'Outgoing Conv',
            dataIndex: 'total_outreports_count',
            sorter: {
                compare: (a, b) => a.total_outreports_count.length - b.total_outreports_count.length,
            },
        },
        {
            title: 'Marketing',
            dataIndex: 'outreports_category_marketing_count',
            sorter: {
                compare: (a, b) => a.outreports_category_marketing_count.length - b.outreports_category_marketing_count.length,
            },
        },
        {
            title: 'Utility',
            dataIndex: 'outreports_category_utility_count',
            sorter: {
                compare: (a, b) => a.outreports_category_utility_count.length - b.outreports_category_utility_count.length,
            },
        },
        {
            title: 'Auth',
            dataIndex: 'outreports_category_auth_count',
            sorter: {
                compare: (a, b) => a.outreports_category_auth_count.length - b.outreports_category_auth_count.length,
            },
        },
        {
            title: 'Service',
            dataIndex: 'outreports_category_service_count',
            sorter: {
                compare: (a, b) => a.outreports_category_service_count.length - b.outreports_category_service_count.length,
            },
        },
    ], []);

    const HandleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            const [start, end] = dates;
            setStartDate(dayjs(start).format('DD/MM/YY'));
            setEndDate(dayjs(end).format('DD/MM/YY'));
        } else {
            setStartDate('');
            setEndDate('');
        }
    }
    return (
        <Card bodyStyle={{ padding: '0px' }}>
            <div className="container-fluid">
                <Flex justifyContent="space-between" alignItems="center" className="py-3">
                    <h2>Live Status</h2>
                    <div className="d-flex gap-3  mb-3 text-right">
                        <Form.Item label="Date Range">
                            <RangePicker
                                onChange={(dates) => HandleDateRangeChange(dates)}
                            />
                        </Form.Item>
                        <Button icon={<CloudDownloadOutlined />} onClick={() => DownloadExcelFile(liveStatus, 'live-status.xlsx')}>Export Live Status</Button>
                    </div>
                </Flex>
            </div>
            <div className="table-responsive">
                <Table
                    columns={tableColumns}
                    expandable={{
                        expandedRowRender: (item) => {
                            const messageStats = [
                                { title: "Pending messages count", icon: faCircleExclamation, color: '#f5a623', count: item?.pendingCount || 0 },
                                { title: "Sent messages count", icon: faCheck, color: undefined, count: item?.sentCount || 0 },
                                { title: "Delivered messages count", icon: faCheckDouble, color: undefined, count: item?.deliveredCount || 0 },
                                { title: "Read messages count", icon: faCheckDouble, color: '#22af4b', count: item?.readCount || 0 },
                                { title: "Failed messages count", icon: faCircleExclamation, color: 'danger', count: item?.failCount || 0 }
                            ];

                            return (
                                <Flex justifyContent="center">
                                    {messageStats?.map((stat, index) => (
                                        <Tooltip key={index} title={stat.title}>
                                            <Flex alignItems="center" className='mr-5'>
                                                <FontAwesomeIcon
                                                    icon={stat.icon}
                                                    style={stat.color && !stat.color.includes('danger') ? { color: stat.color } : undefined}
                                                    className={stat.color === 'danger' ? 'text-danger' : ''}
                                                />
                                                <p className="m-0 pl-2">{stat.count}</p>
                                            </Flex>
                                        </Tooltip>
                                    ))}
                                </Flex>
                            );
                        },
                    }}
                    loading={loading}
                    dataSource={filterData?.length > 0 ? filterData : liveStatus}
                    rowKey="id"
                    summary={() => {
                        const data = filterData?.length > 0 ? filterData : liveStatus;

                        const totalIncomingReports = data.reduce(
                            (sum, record) => sum + (Number(record.total_incomingreports_count) || 0),
                            0
                        );
                        const totalCampaignReports = data.reduce(
                            (sum, record) => sum + (Number(record.total_campaignreports_count) || 0),
                            0
                        );
                        const totalOutReports = data.reduce(
                            (sum, record) => sum + (Number(record.total_outreports_count) || 0),
                            0
                        );
                        const totalMarketing = data.reduce(
                            (sum, record) => sum + (Number(record.outreports_category_marketing_count) || 0),
                            0
                        );
                        const totalUtility = data.reduce(
                            (sum, record) => sum + (Number(record.outreports_category_utility_count) || 0),
                            0
                        );
                        const totalService = data.reduce(
                            (sum, record) => sum + (Number(record.outreports_category_service_count) || 0),
                            0
                        );
                        const totalAuth = data.reduce(
                            (sum, record) => sum + (Number(record.outreports_category_auth_count) || 0),
                            0
                        );
                        return (
                            <Table.Summary.Row style={{ backgroundColor: '#f0f0f0' }}>
                                <Table.Summary.Cell index={0} colSpan={1}>
                                    <strong>Totals:</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} style={{ fontWeight: 'bold' }} />
                                <Table.Summary.Cell index={2} style={{ fontWeight: 'bold' }} />
                                <Table.Summary.Cell index={3} style={{ fontWeight: 'bold' }}>
                                    <strong>{totalIncomingReports}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4} style={{ fontWeight: 'bold' }}>
                                    <strong>{totalCampaignReports}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5} style={{ fontWeight: 'bold' }}>
                                    <strong>{totalOutReports}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6} style={{ fontWeight: 'bold' }}>
                                    <strong>{totalMarketing}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={7} style={{ fontWeight: 'bold' }}>
                                    <strong> {totalUtility}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8} style={{ fontWeight: 'bold' }}>
                                    <strong> {totalAuth}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8} style={{ fontWeight: 'bold' }}>
                                    <strong> {totalService}</strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        );
                    }}

                />
            </div>
        </Card >
    );
};

export default LiveStatus
